import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, Loader2, Radio, Mic, ChevronRight, RefreshCw, Users } from 'lucide-react';
import { useKiooAudio } from '../AudioProvider';
import { useLiveListeners } from '../useLiveListeners';
import StationPicker from '../StationPicker';
import { useT } from '../i18n';
import {
  getCurrentProgram,
  getLanguageFlag,
  getProgramTypeIcon,
} from '../../utils/scheduleUtils';

const useNowOnAir = () => {
  const [info, setInfo] = useState(() => getCurrentProgram());
  useEffect(() => {
    const tick = () => setInfo(getCurrentProgram());
    const id = setInterval(tick, 30_000); // refresh every 30s (spec §2)
    tick();
    return () => clearInterval(id);
  }, []);
  return info;
};

const OnAirCard = ({ program, label, testid }) => {
  if (!program) return null;
  const name = program.name || program.Program;
  const start = program.startTime || program['Start Time'];
  const end = program.endTime || program['End Time'];
  const language = program.language || program.Language;
  const type = program.type || program.Type;
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-white border border-emerald-100 p-4 shadow-sm"
    >
      <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
        {label}
      </div>
      <div className="mt-1 text-lg font-extrabold text-gray-900 leading-tight break-words">
        {getProgramTypeIcon(type)} {name}
      </div>
      <div className="mt-1 text-sm text-gray-600">
        {start} – {end} GMT
        {language && (
          <span className="ml-2">
            {getLanguageFlag(language)} {language}
          </span>
        )}
      </div>
    </div>
  );
};

const MobileHome = () => {
  const t = useT();
  const { isPlaying, isBuffering, error, play, pause, reload, currentStation } =
    useKiooAudio();
  const info = useNowOnAir();
  const { counts: listenerCounts } = useLiveListeners();
  const listenersHere = currentStation
    ? listenerCounts[currentStation.slug] || 0
    : 0;

  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const buttonState = useMemo(() => {
    if (isBuffering) return 'buffering';
    if (isPlaying) return 'playing';
    return 'idle';
  }, [isPlaying, isBuffering]);

  const handleToggle = () => {
    if (isPlaying) pause();
    else play();
  };

  return (
    <div className="px-4 pt-4">
      {/* Big Listen Live Card - themed by selected station */}
      <section
        data-testid="mobile-live-player-card"
        className="rounded-3xl text-white p-5 shadow-lg overflow-hidden relative"
        style={{
          background:
            'linear-gradient(135deg, var(--kioo-primary-dark) 0%, var(--kioo-primary) 55%, var(--kioo-accent) 100%)',
        }}
      >
        {/* Big transparent background logo — centered watermark that fills
            the card and reinforces the current station's identity. */}
        {currentStation?.logo_url && (
          <img
            src={currentStation.logo_url}
            alt=""
            aria-hidden="true"
            data-testid="mobile-live-player-bg-logo"
            className="absolute inset-0 m-auto w-[85%] h-[85%] object-contain opacity-20 pointer-events-none select-none"
            style={{ mixBlendMode: 'screen' }}
          />
        )}
        <div className="relative z-10 flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${
              isPlaying ? 'bg-red-500' : 'bg-white/25'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isPlaying ? 'bg-white animate-pulse' : 'bg-white/70'
              }`}
            />
            {isPlaying ? 'LIVE' : 'READY'}
          </span>
          {/* Live listener count for the currently selected station */}
          <span
            data-testid="mobile-home-listeners"
            title={`${listenersHere} live listener${listenersHere === 1 ? '' : 's'}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/25 text-white text-[10px] font-bold"
          >
            <Users className="w-3 h-3" />
            {listenersHere.toLocaleString()}
          </span>
          <div className="ml-auto">
            <StationPicker variant="hero" />
          </div>
        </div>

        <div className="relative z-10 mt-3 flex items-center gap-3">
          {currentStation?.logo_url && (
            <img
              src={currentStation.logo_url}
              alt={currentStation.name}
              className="w-16 h-16 object-contain flex-shrink-0"
              style={{ background: 'transparent' }}
              loading="eager"
              data-testid="mobile-home-station-logo"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold leading-tight truncate">
              {currentStation?.name || t('home.title')}
            </h1>
            <p className="text-xs text-white/90 truncate">
              {currentStation?.frequency && (
                <span className="font-bold mr-1">
                  {currentStation.frequency}
                </span>
              )}
              · {currentStation?.tagline || t('home.subtitle')}
            </p>
          </div>
        </div>

        {currentStation && !currentStation.has_own_stream && currentStation.relay_note && (
          <div
            data-testid="mobile-home-relay-note"
            className="relative z-10 mt-3 text-[11px] bg-black/40 rounded-lg px-3 py-1.5"
          >
            🔁 {currentStation.relay_note}
          </div>
        )}

        {/* Play/Pause button */}
        <div className="relative z-10 mt-5 flex items-center gap-3">
          <button
            data-testid="mobile-home-play-btn"
            onClick={handleToggle}
            aria-label={isPlaying ? t('home.pause') : t('home.listenLive')}
            className={`inline-flex items-center gap-3 px-6 py-4 rounded-full font-bold text-base shadow-lg transition-transform active:scale-95 ${
              isPlaying
                ? 'bg-white text-red-600'
                : 'bg-white text-emerald-700'
            }`}
          >
            {buttonState === 'buffering' ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('home.buffering')}
              </>
            ) : buttonState === 'playing' ? (
              <>
                <Pause className="w-5 h-5" />
                {t('home.pause')}
              </>
            ) : (
              <>
                <Play className="w-5 h-5 fill-current" />
                {t('home.listenLive')}
              </>
            )}
          </button>

          {error && (
            <button
              data-testid="mobile-home-reload-btn"
              onClick={reload}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-white/20 text-white text-xs font-semibold"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
        </div>

        {error && (
          <div
            data-testid="mobile-home-error"
            className="relative z-10 mt-3 text-xs bg-black/40 rounded-lg px-3 py-2"
          >
            {t('home.error')}
          </div>
        )}

        {!online && (
          <div
            data-testid="mobile-home-offline"
            className="relative z-10 mt-3 text-xs bg-black/40 rounded-lg px-3 py-2"
          >
            {t('home.offline')}
          </div>
        )}
      </section>

      {/* Now On Air + Up Next — only Kioo has a scheduled programme grid today.
          For VOX / Kilikezu we show a "Coming Soon" card instead of leaking
          the Kioo schedule to those stations. */}
      <section className="mt-4 space-y-3">
        {currentStation?.slug !== 'kioo' ? (
          <div
            data-testid="mobile-home-coming-soon"
            className="rounded-2xl bg-white border-2 p-5 text-center shadow-sm"
            style={{ borderColor: 'var(--kioo-primary)' }}
          >
            <div
              className="text-[11px] font-bold uppercase tracking-wider"
              style={{ color: 'var(--kioo-primary)' }}
            >
              {t('home.nowOnAir')}
            </div>
            <div
              className="mt-2 text-2xl font-extrabold"
              style={{ color: 'var(--kioo-primary-dark)' }}
            >
              Coming Soon!
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {currentStation?.name} programming schedule will be published here.
            </p>
            {currentStation?.relay_note && (
              <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                🔁 {currentStation.relay_note}
              </div>
            )}
          </div>
        ) : (
          <>
            {info?.current ? (
              <OnAirCard
                program={info.current}
                label={t('home.nowOnAir')}
                testid="mobile-home-now-on-air"
              />
            ) : (
              <div className="rounded-2xl bg-white border border-gray-100 p-4 text-sm text-gray-600">
                {t('home.nowOnAir')}: Kioo Radio 98.1 FM — {t('home.subtitle')}
              </div>
            )}
            {info?.next && (
              <OnAirCard
                program={info.next}
                label={t('home.upNext')}
                testid="mobile-home-up-next"
              />
            )}
          </>
        )}
      </section>

      {/* Send Voice Message CTA */}
      <section className="mt-5">
        <Link
          to="/app/message"
          data-testid="mobile-home-send-voice-cta"
          className="flex items-center gap-4 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white p-4 shadow-md active:scale-[0.98] transition-transform"
        >
          <div className="w-12 h-12 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
            <Mic className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-base leading-tight">
              🎙 {t('home.sendVoice')}
            </div>
            <div className="text-xs opacity-90 mt-0.5 leading-tight">
              {t('home.sendVoiceSub')}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 opacity-80" />
        </Link>
      </section>

      {/* Quick links */}
      <section className="mt-5 grid grid-cols-2 gap-3">
        <Link
          to="/app/programs"
          data-testid="mobile-home-programs-link"
          className="rounded-2xl bg-white border border-gray-100 p-4 shadow-sm active:scale-[0.98] transition-transform"
        >
          <div className="text-2xl">📅</div>
          <div className="mt-1 font-bold text-sm text-gray-900">
            {t('nav.programs')}
          </div>
          <div className="text-[11px] text-gray-500">
            See the full weekly schedule
          </div>
        </Link>
        <Link
          to="/app/contact"
          data-testid="mobile-home-contact-link"
          className="rounded-2xl bg-white border border-gray-100 p-4 shadow-sm active:scale-[0.98] transition-transform"
        >
          <div className="text-2xl">☎️</div>
          <div className="mt-1 font-bold text-sm text-gray-900">
            {t('nav.contact')}
          </div>
          <div className="text-[11px] text-gray-500">
            Call, WhatsApp or email
          </div>
        </Link>
      </section>
    </div>
  );
};

export default MobileHome;
